import { Controller } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import Dashboard from "@uppy/dashboard"
import ImageEditor from "@uppy/image-editor"
import ActiveStorageUpload from "../vendor/uppy_activestorage_upload"

export default class extends Controller {
  static targets = ["trigger", "preview", "field", "status"]
  static values = {
    fieldName: String,
    currentUrl: { type: String, default: "" },
    editImages: { type: Boolean, default: true },
  }

  connect() {
    if (this.hasPreviewTarget && this.currentUrlValue) {
      this.previewTarget.src = this.currentUrlValue
    }

    this.uppy = new Uppy({
      autoProceed: false,
      allowMultipleUploads: false,
      allowMultipleUploadBatches: false,
      restrictions: { maxNumberOfFiles: 1 },
    })

    const dashboardOpts = {
      trigger: this.triggerTarget,
      closeAfterFinish: true,
      closeModalOnClickOutside: true,
      proudlyDisplayPoweredByUppy: false,
    }

    if (this.editImagesValue) {
      dashboardOpts.autoOpen = "imageEditor"
      dashboardOpts.plugins = ["ImageEditor"]
      this.uppy.use(Dashboard, dashboardOpts)
      this.uppy.use(ImageEditor)
      this.uppy.on("file-editor:complete", () => this.uppy.upload())
      this.uppy.on("files-added", (files) => {
        const imageEditor = this.uppy.getPlugin("ImageEditor")
        const file = files[0]
        if (file && !imageEditor?.canEditFile(file)) {
          this.uppy.upload()
        }
      })
    } else {
      this.uppy.use(Dashboard, dashboardOpts)
      this.uppy.on("files-added", () => this.uppy.upload())
    }

    this.uppy.use(ActiveStorageUpload, {
      directUploadUrl: this.directUploadUrl,
    })
    this.uppy.on("complete", this.onComplete.bind(this))
  }

  disconnect() {
    this.uppy?.destroy()
  }

  onComplete(result) {
    result.successful.forEach((file) => {
      this.setField(file)
      this.setPreview(file)
      this.setStatus(file)
    })
  }

  setField(file) {
    const signedId =
      file.response?.directUploadSignedId || file.response?.signed_id
    if (!signedId) return

    if (this.hasFieldTarget) {
      this.fieldTarget.name = this.fieldNameValue
      this.fieldTarget.value = signedId
    } else {
      const field = document.createElement("input")
      field.type = "hidden"
      field.dataset.uppySingleFileUploadTarget = "field"
      field.name = this.fieldNameValue
      field.value = signedId
      this.element.appendChild(field)
    }
  }

  setPreview(file) {
    if (!this.hasPreviewTarget) return
    if (!file.type?.startsWith("image/")) return

    const src = file.preview || URL.createObjectURL(file.data)
    this.previewTarget.src = src
    this.previewTarget.style.display = "block"
  }

  setStatus(file) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = `Ready: ${file.name}`
  }

  get directUploadUrl() {
    return document
      .querySelector("meta[name='direct-upload-url']")
      ?.getAttribute("content")
  }
}
